import { Checkbox, Table, Input, Button, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./MessageBox.scss";
import { MessageDataType, MenuProps } from "./types";

import { header } from "../utils/salesAdminHeader";

import ContentTitle from "../../../components/ui/ContentTitle";
import { apiCall } from "../../../utils/apiCall";
import GeneralModal from "../../../utils/GeneralModal";

//Permission usage
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

const { Search } = Input;

const MessageBox = ({ setSelectedService, setSelectSubItem }: MenuProps) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<MessageDataType[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState<number[]>([]);
  const [searchedMessages, setSearchedMessages] = useState<MessageDataType[]>(
    []
  );
  const [isSearching, setIsSearching] = useState(false);
  const isAllSelected =
    messages.length > 0 &&
    messages.every((item) => selectedMessages.includes(item.mailId));

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const onSearch = (value: string) => {
    if (value) {
      setIsSearching(true);
      setSearchedMessages(
        messages.filter(
          (message) =>
            message.title.includes(value) ||
            message.receiver.includes(value) ||
            dayjs(message.sendDate.split("T")[0])
              .format("YYYYMMDD")
              .includes(value)
        )
      );
    } else {
      setIsSearching(false);
    }
  };

  const fetchMessages = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint: process.env.REACT_APP_SALES_ADMIN + "/contents/admin/mails",
    });
    if (data) {
      setMessages(data);
    }
  };

  const handleTitleCheckboxChange = (e: any) => {
    if (e.target.checked) {
      setSelectedMessages(messages.map((item) => item.mailId));
    } else {
      setSelectedMessages([]);
    }
  };

  const handleRowCheckboxChange = (mailId: number) => {
    setSelectedMessages((prev) => {
      if (prev.includes(mailId)) {
        return prev.filter((item) => item !== mailId);
      } else {
        return [...prev, mailId];
      }
    });
  };

  const handleDelete = async () => {
    await apiCall({
      method: "PATCH",
      header: header,
      payload: {
        ...(selectedMessages.length > 0 && { mailIds: selectedMessages }),
      },
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + "/contents/admin/mails/deleted",
    });
    setModalOpen(false);
    setSelectedMessages([]);
    fetchMessages();
  };

  const columns: ColumnsType<MessageDataType> = [
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={selectedMessages.length > 0 && !isAllSelected}
          onChange={handleTitleCheckboxChange}
        />
      ),
      fixed: "left",
      key: "check",
      dataIndex: "mailId",
      render: (mailId: number) => (
        <Checkbox
          checked={selectedMessages.includes(mailId)}
          onChange={() => handleRowCheckboxChange(mailId)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: 55,
    },
    {
      title: "제목",
      key: "title",
      dataIndex: "title",
      width: 440,
    },
    {
      title: "수신인",
      key: "receiver",
      dataIndex: "receiver",
      render: (v, record) => `${v}(${record.companyNumber})`,
      width: 110,
    },
    {
      title: "발신일",
      key: "sendDate",
      dataIndex: "sendDate",
      render: (v) => v.replace("T", " ").split(".")[0],
      sorter: (a, b) =>
        dayjs(b.sendDate.split("T")[0]).valueOf() -
        dayjs(a.sendDate.split("T")[0]).valueOf(),
      width: 160,
    },
    {
      title: "상태",
      key: "isRead",
      dataIndex: "isRead",
      render: (v, record) =>
        v
          ? `읽음 ${record.readDate.replace("T", " ").split(".")[0]}`
          : "읽지않음",
      width: 160,
    },
  ];

  useEffect(() => {
    fetchMessages();
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
  }, []);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="messageBoxWrapper">
      <ContentTitle title="메세지함" />
      <div className="content-container">
        <div
          className={`flex mb-5 ${
            selectedMessages.length > 0 ? "justify-between" : "justify-end"
          }`}
        >
          {selectedMessages.length > 0 && (
            <div className="flex justify-start">
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                삭제
              </Button>
            </div>
          )}
          <div className="flex justify-end">
            <Search
              placeholder="제목이나 게시자 검색"
              allowClear
              onSearch={onSearch}
            />
            <Button
              icon={<PlusOutlined />}
              type="primary"
              className="mx-5"
              onClick={() => {
                navigate("/sales188/create-message");
              }}
            >
              작성
            </Button>
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={isSearching ? searchedMessages : messages}
          scroll={{ x: "max-content", y: 425 }}
          onRow={(record) => {
            return {
              onClick: () => {
                navigate(`/sales188/message-detail/${record.mailId}`);
              },
              style: { cursor: "pointer" },
            };
          }}
        />
      </div>
      <GeneralModal
        open={modalOpen}
        close={() => {
          setModalOpen(false);
        }}
        width={400}
        isDeletion={false}
        isConfirm
        noBtnText="취소"
        confirmBtnText="확인"
        title={`선택된 메세지 ${selectedMessages.length}개를 삭제할까요?`}
        text="삭제된 메세지는 복구할 수 없습니다."
        handleConfirm={handleDelete}
      />
    </div>
  );
};

export default MessageBox;
